import type { ConversationMessage } from '../types/conversation.js';
import type { GoalBreakdown } from './morning-check-api.js';

const BREAKDOWN_URL = '/api/breakdown';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ─── Types ──────────────────────────────────────────────────────────

export type ConversationStatus = 'active' | 'completed' | 'cancelled';
export type BreakdownLevel = 'monthly' | 'weekly' | 'daily';
export type BreakdownStatus = 'open' | 'in_progress' | 'done' | 'skipped';

export interface Conversation {
  id: number;
  type: BreakdownLevel;
  period: string;
  status: ConversationStatus;
  created_at: string;
  updated_at: string;
}

export interface StartConversationResponse {
  conversation: Conversation;
  firstMessage?: ConversationMessage;
  warning?: string;
}

export interface SendMessageResponse {
  userMessage: ConversationMessage;
  assistantMessage: ConversationMessage;
}

export interface ConfirmBreakdownsResponse {
  conversation: Conversation;
  breakdowns: GoalBreakdown[];
}

export interface ConversationWithMessages {
  conversation: Conversation;
  messages: ConversationMessage[];
}

export interface BreakdownHierarchyNode {
  id: number;
  level: BreakdownLevel;
  title: string;
  description: string | null;
  priority: string;
  status: BreakdownStatus;
  children: BreakdownHierarchyNode[];
}

// ─── Conversation API ───────────────────────────────────────────────

export async function startConversation(
  type: BreakdownLevel,
  period: string,
): Promise<StartConversationResponse> {
  return fetchJson<StartConversationResponse>(`${BREAKDOWN_URL}/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, period }),
  });
}

export async function sendMessage(
  conversationId: number,
  content: string,
): Promise<SendMessageResponse> {
  return fetchJson<SendMessageResponse>(
    `${BREAKDOWN_URL}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
}

export async function getConversation(id: number): Promise<ConversationWithMessages> {
  return fetchJson<ConversationWithMessages>(`${BREAKDOWN_URL}/conversations/${id}`);
}

export async function getConversationByPeriod(
  type: BreakdownLevel,
  period: string,
): Promise<ConversationWithMessages> {
  return fetchJson<ConversationWithMessages>(
    `${BREAKDOWN_URL}/conversations?type=${encodeURIComponent(type)}&period=${encodeURIComponent(period)}`,
  );
}

export async function confirmBreakdowns(
  conversationId: number,
): Promise<ConfirmBreakdownsResponse> {
  return fetchJson<ConfirmBreakdownsResponse>(
    `${BREAKDOWN_URL}/conversations/${conversationId}/confirm`,
    { method: 'POST' },
  );
}

// ─── Goal API ───────────────────────────────────────────────────────

export async function updateGoalStatus(
  goalId: number,
  status: BreakdownStatus,
): Promise<GoalBreakdown> {
  return fetchJson<GoalBreakdown>(`${BREAKDOWN_URL}/goals/${goalId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export async function updateGoal(
  goalId: number,
  updates: { title?: string; description?: string; priority?: string; agent_id?: number | null },
): Promise<GoalBreakdown> {
  return fetchJson<GoalBreakdown>(`${BREAKDOWN_URL}/goals/${goalId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteAllGoals(): Promise<{ deleted: number }> {
  return fetchJson<{ deleted: number }>(`${BREAKDOWN_URL}/goals`, {
    method: 'DELETE',
  });
}

// ─── Hierarchy API ──────────────────────────────────────────────────

export async function getHierarchy(): Promise<BreakdownHierarchyNode[]> {
  return fetchJson<BreakdownHierarchyNode[]>(`${BREAKDOWN_URL}/hierarchy`);
}
